// packages/shared/src/currency.ts
//
// Currency formatting / parsing helpers — node-pure (no Intl polyfills, relies
// on the host runtime's `Intl.NumberFormat`). All monetary values flow through
// this module as integer **cents** (..) so we never round-trip floats
// across the wire.
//
// Per PLAN Task 39 + AC-28:
//   formatCents(1099)           → "$10.99"
//   formatCents(1099, 'EUR')    → "€10.99"  (locale-default Intl style)
//   formatCents(0, 'USD')       → "$0.00"
//   formatCents(-500, 'USD')    → "-$5.00"
//   parseCents("$10.99")        → 1099
//   parseCents("10,99", 'EUR')  → 1099
//
// FIX-13 boundary: NO Fastify / Mongoose imports. Safe to bundle in Vite.
// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------
/**
 * Most ISO 4217 currencies use 2 decimal places ("minor units = cents"). A
 * handful use 0 (JPY, KRW, VND, ...) and a few use 3 (BHD, KWD, ...). The
 * Phase 1 monetary rail is USD-only; this map covers the common cases that
 * Phase 2+ will need so we don't ship buggy formatting later.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
    'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);
const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);
function decimalsFor(currency) {
    const upper = currency.toUpperCase();
    if (ZERO_DECIMAL_CURRENCIES.has(upper))
        return 0;
    if (THREE_DECIMAL_CURRENCIES.has(upper))
        return 3;
    return 2;
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Format an integer cents value as a localized currency string.
 *
 * Invariants:
 *   - `cents` MUST be an integer; non-integers throw to surface upstream
 *     rounding bugs early (catalog price math is frequently the culprit).
 *   - The display string is locale-canonical — symbol position, separators,
 *     and grouping all match `Intl.NumberFormat` for the locale.
 *
 * @example
 *   formatCents(1099)                      // "$10.99"
 *   formatCents(1099, 'EUR')               // "€10.99"
 *   formatCents(1500000, 'VND', 'vi-VN')   // "1.500.000 ₫"
 */
export function formatCents(cents, currency = 'USD', locale = 'en-US', options = {}) {
    if (!Number.isFinite(cents) || !Number.isInteger(cents)) {
        throw new TypeError(`formatCents: cents must be a finite integer (got ${cents})`);
    }
    const decimals = decimalsFor(currency);
    const major = cents / 10 ** decimals;
    const fmt = new Intl.NumberFormat(options.locale ?? locale, {
        style: 'currency',
        currency: (options.currency ?? currency).toUpperCase(),
        minimumFractionDigits: options.minimumFractionDigits ?? decimals,
        maximumFractionDigits: options.maximumFractionDigits ?? decimals,
    });
    return fmt.format(major);
}
/**
 * Parse a human-typed currency string back into integer cents. Tolerant of
 * symbols, grouping separators (`,` / `.` / spaces), and either decimal mark
 * (`.` or `,` — disambiguated by position).
 *
 * Returns `null` when the input cannot be parsed (callers typically wrap in
 * a Zod transform that maps null → validation error).
 *
 * @example
 *   parseCents('$10.99')        // 1099
 *   parseCents('10,99', 'EUR')  // 1099
 *   parseCents('1.500.000 ₫', 'VND') // 1500000
 *   parseCents('garbage')       // null
 */
export function parseCents(raw, currency = 'USD') {
    if (typeof raw !== 'string')
        return null;
    // Strip everything that isn't a digit, separator, or sign.
    const cleaned = raw
        .replace(/[^\d,.\-]/g, '')
        .trim();
    if (cleaned.length === 0)
        return null;
    // Disambiguate decimal vs grouping by counting separator occurrences.
    // - If both `,` and `.` appear → the LAST occurrence is the decimal mark.
    // - If only one separator appears → treat it as decimal IF it has 1-3
    //   trailing digits, else as grouping.
    let normalized = cleaned;
    const lastDot = cleaned.lastIndexOf('.');
    const lastComma = cleaned.lastIndexOf(',');
    if (lastDot >= 0 && lastComma >= 0) {
        const decimalIdx = Math.max(lastDot, lastComma);
        const decimalChar = cleaned[decimalIdx];
        const groupChar = decimalChar === '.' ? ',' : '.';
        normalized = cleaned.replace(new RegExp(`\\${groupChar}`, 'g'), '');
        if (decimalChar === ',')
            normalized = normalized.replace(',', '.');
    }
    else if (lastDot >= 0) {
        const tail = cleaned.length - lastDot - 1;
        if (tail > 3)
            normalized = cleaned.replace(/\./g, '');
    }
    else if (lastComma >= 0) {
        const tail = cleaned.length - lastComma - 1;
        normalized = tail <= 3
            ? cleaned.replace(',', '.')
            : cleaned.replace(/,/g, '');
    }
    const n = Number(normalized);
    if (!Number.isFinite(n))
        return null;
    const decimals = decimalsFor(currency);
    return Math.round(n * 10 ** decimals);
}
//# sourceMappingURL=currency.js.map